import { Alert, Button, Card, DatePicker, Drawer, Form, Grid, Input, Radio, Select, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Dayjs } from "dayjs";
import { isProblemDetails, problemMessages, type components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";

type FamilyDetails = components["schemas"]["FamilyDetails"];
type Relationship = components["schemas"]["GuardianRelationship"];

const relationships: Relationship[] = ["Mother", "Father", "Grandparent", "Sibling", "LegalGuardian", "Other"];

interface GuardianForm {
  fullName: string;
  phone: string;
  relationship: Relationship;
}

interface FamilyForm {
  name?: string;
  guardians: GuardianForm[];
  payerIndex: number;
  student: { firstName: string; lastName: string; dateOfBirth?: Dayjs };
}

interface Props {
  open: boolean;
  onClose(): void;
  onCreated(family: FamilyDetails): void;
}

export function CreateFamilyDrawer({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FamilyForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (values: FamilyForm) => {
      const { data, error, response } = await api.POST("/families", {
        body: {
          ...(values.name?.trim() ? { name: values.name.trim() } : {}),
          guardians: values.guardians.map((g, index) => ({
            fullName: g.fullName.trim(),
            phone: g.phone.trim(),
            relationship: g.relationship,
            isPayer: index === values.payerIndex,
          })),
          students: [
            {
              firstName: values.student.firstName.trim(),
              lastName: values.student.lastName.trim(),
              dateOfBirth: values.student.dateOfBirth ? values.student.dateOfBirth.format("YYYY-MM-DD") : null,
            },
          ],
        },
      });
      if (!data) {
        throw Object.assign(new Error(String(response.status)), { problem: isProblemDetails(error) ? error : undefined });
      }
      return data;
    },
    onSuccess: async (family) => {
      await queryClient.invalidateQueries({ queryKey: ["families"] });
      form.resetFields();
      setServerErrors([]);
      onCreated(family);
    },
    onError: (err: Error & { problem?: components["schemas"]["ProblemDetails"] }) => {
      setServerErrors(problemMessages(err.problem as never, t("app.error")));
    },
  });

  return (
    <Drawer
      title={t("families.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 560 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("family.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("family.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<FamilyForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ guardians: [{ relationship: "Mother" }], payerIndex: 0, student: {} }}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item name="name" label={t("family.name")} extra={t("family.nameHint")}>
          <Input maxLength={200} />
        </Form.Item>

        <Typography.Title level={5}>{t("family.guardians")}</Typography.Title>
        <Form.Item name="payerIndex" label={t("family.payer")} extra={t("family.payerHint")} rules={[{ required: true, message: t("family.validation.onePayer") }]}>
          <Form.List name="guardians">
            {(fields, { add, remove }) => (
              <Space orientation="vertical" style={{ width: "100%" }}>
                <Radio.Group
                  style={{ width: "100%" }}
                  value={form.getFieldValue("payerIndex") as number}
                  onChange={(e) => form.setFieldValue("payerIndex", e.target.value as number)}
                >
                  <Space orientation="vertical" style={{ width: "100%" }}>
                    {fields.map((field, index) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={
                          <Radio value={index}>
                            {t("family.guardian")} {index + 1} — {t("family.payer")}
                          </Radio>
                        }
                        extra={
                          fields.length > 1 && (
                            <Button type="text" danger icon={<DeleteOutlined />} aria-label={t("family.removeGuardian")} onClick={() => remove(field.name)} />
                          )
                        }
                      >
                        <Form.Item name={[field.name, "fullName"]} label={t("family.fullName")} rules={[{ required: true, message: t("family.validation.required") }]}>
                          <Input maxLength={200} />
                        </Form.Item>
                        <div style={{ display: "grid", gridTemplateColumns: screens.sm ? "1fr 1fr" : "1fr", gap: 12 }}>
                          <Form.Item
                            name={[field.name, "phone"]}
                            label={t("family.phone")}
                            rules={[
                              { required: true, message: t("family.validation.required") },
                              { pattern: /^[\d\s()+-]{9,20}$/, message: t("family.validation.phone") },
                            ]}
                          >
                            <Input inputMode="tel" placeholder={t("family.phoneHint")} />
                          </Form.Item>
                          <Form.Item name={[field.name, "relationship"]} label={t("family.relationship")}>
                            <Select options={relationships.map((r) => ({ value: r, label: t(`family.relationships.${r}`) }))} />
                          </Form.Item>
                        </div>
                      </Card>
                    ))}
                  </Space>
                </Radio.Group>
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ relationship: "Other" })} block>
                  {t("family.addGuardian")}
                </Button>
              </Space>
            )}
          </Form.List>
        </Form.Item>

        <Typography.Title level={5}>{t("family.student")}</Typography.Title>
        <div style={{ display: "grid", gridTemplateColumns: screens.sm ? "1fr 1fr" : "1fr", gap: 12 }}>
          <Form.Item name={["student", "firstName"]} label={t("family.firstName")} rules={[{ required: true, message: t("family.validation.required") }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name={["student", "lastName"]} label={t("family.lastName")} rules={[{ required: true, message: t("family.validation.required") }]}>
            <Input maxLength={100} />
          </Form.Item>
        </div>
        <Form.Item name={["student", "dateOfBirth"]} label={t("family.dateOfBirth")}>
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
